import React from 'react'
import { useParams } from 'react-router-dom'

import {
  Avatar,
  Box,
  Button,
  InfoDot,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  BudgetEmployeesDatatableQuery,
  ProjectionKind,
  useBudgetEmployeesDatatable,
} from 'generated/resources/datatable.graphql'
import {
  AllocationFragment,
  BudgetFragment,
  EmployeesTeamsQuery,
} from 'generated/resources/graphql'

import DataTable, { useDataTable } from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import AllocationPerEmployeeModal from 'modules/finance/components/Budgets/Detail/AllocationPerEmployeeModal'
import DeleteEmployeeModal from 'modules/finance/components/Budgets/Detail/DeleteEmployeeModal'
import RemoveAllocationModal from 'modules/finance/components/Budgets/Detail/RemoveAllocationModal'
import { allocationStatusColor } from 'modules/finance/lib/budgetTagInfo'
import { allocationsWithAmountIds } from 'modules/finance/lib/budget_allocations'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

import DataTableHeader from './Datatable/Header'
import EmployeeDetail from './Detail'
import EmptyEmployees from './Empty'
import TeamFilter from './Filters/team'

type TeamsTeam = EmployeesTeamsQuery['teams']['teams'][number]

interface EmployeeTabProps {
  teams: TeamsTeam[]
  currency: string
  periodStart?: string
  periodEnd?: string
  budget: BudgetFragment
  openEmployeeModal: () => void
  goToRecordsTab: () => void
  allocations: AllocationFragment[]
  refetchBudget: (param: { id: string; periodStart: string }) => void
}

interface DatatableDataContainerProps {
  openDeleteModal: boolean
  openAllocationPerEmployeeModal: boolean
  openRemoveAllocationModal: boolean
  handleCloseDeleteModal: () => void
  handleCloseAllocationPerEmployeeModal: () => void
  handleRemoveAllocationModal: () => void
  budget: BudgetFragment
  allocations: AllocationFragment[]
  allocationId?: string
  refetchBudget: (param: { id: string; periodStart: string }) => void
}

const DatatableDataContainer = ({
  openDeleteModal,
  openAllocationPerEmployeeModal,
  openRemoveAllocationModal,
  handleCloseDeleteModal,
  handleCloseAllocationPerEmployeeModal,
  handleRemoveAllocationModal,
  budget,
  allocationId,
  allocations,
  refetchBudget,
}: DatatableDataContainerProps) => {
  const response = useDataTable<BudgetEmployeesDatatableQuery>()
  const allocationIds = Array.from(response.selectedRows)

  const allocationsWithAmountIdsArray = response ? allocationsWithAmountIds(response.data) : []

  const allocationsToRemoveIds = allocationIds.filter((id) =>
    allocationsWithAmountIdsArray.includes(id)
  )

  const budgetId = budget.id

  return (
    <>
      {openDeleteModal && (
        <DeleteEmployeeModal
          closeModal={handleCloseDeleteModal}
          budgetId={String(budgetId)}
          allocationIds={allocationId ? [allocationId] : allocationIds}
          periodStart={budget.currentPeriodStart}
          refetchBudget={refetchBudget}
        />
      )}
      {openAllocationPerEmployeeModal && (
        <AllocationPerEmployeeModal
          closeModal={handleCloseAllocationPerEmployeeModal}
          currency={budget.currency}
          budgetId={String(budgetId)}
          budgetLimit={budget.amountCents}
          allocationIds={allocationIds}
          allocations={allocations}
          periodStart={budget.currentPeriodStart}
          refetchBudget={refetchBudget}
        />
      )}

      {openRemoveAllocationModal && (
        <RemoveAllocationModal
          periodStart={budget.currentPeriodStart}
          budgetId={String(budgetId)}
          allocationIds={allocationsToRemoveIds}
          closeModal={handleRemoveAllocationModal}
          refetchBudget={refetchBudget}
        />
      )}
    </>
  )
}
const EmployeesTab = ({
  teams,
  currency,
  periodStart,
  periodEnd,
  budget,
  openEmployeeModal,
  goToRecordsTab,
  allocations,
  refetchBudget,
}: EmployeeTabProps) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { budgetId } = useParams()

  const [openDeleteModal, toggleDeleteModal] = useToggle(false)
  const [openAllocationPerEmployeeModal, toggleAllocationPerEmployeeModal] = useToggle(false)
  const [openRemoveAllocationModal, toggleRemoveAllocationModal] = useToggle(false)
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const [allocationId, setAllocationId] = React.useState<string>()

  const handleEmployeeDetailClick = (allocationDetailId: string) => {
    setAllocationId(allocationDetailId)
  }

  const source = useBudgetEmployeesDatatable(
    [
      {
        identifier: 'employee',
        colSpan: 4,
        title: i18n.t('budgets.detail.tabs.employees.table.employee'),
        kind: ProjectionKind.Custom,
        render: ({ employee }) => {
          if (!employee) return
          return (
            <Box flexDirection="row" alignItems="center" gap="s16">
              <Avatar label={employee.fullName} url={buildUrl(employee.avatar?.url)} size="s40" />
              <Text.Normal>{employee.fullName}</Text.Normal>
            </Box>
          )
        },
        template: 'employee',
      },
      {
        identifier: 'amountCents',
        title: i18n.t('budgets.detail.tabs.employees.table.allocation'),
        kind: ProjectionKind.Custom,
        justifyContent: 'flexEnd',
        render: ({ amountCents }) => {
          if (!amountCents) return
          return (
            <Box
              textAlign="right"
              aria-label={i18n.t('budgets.detail.tabs.employees.table.allocation')}
            >
              <Text.Normal> {amountWithCurrency(amountCents, currency, locale)}</Text.Normal>
            </Box>
          )
        },
        template: 'assigned',
      },
      {
        identifier: 'reportedAmountCents',
        title: i18n.t('budgets.detail.tabs.employees.table.reported'),
        kind: ProjectionKind.Custom,
        justifyContent: 'flexEnd',
        render: ({ reportedAmountCents }) => {
          return (
            <Box textAlign="right">
              <Text.Normal>
                {' '}
                {amountWithCurrency(reportedAmountCents, currency, locale)}
              </Text.Normal>
            </Box>
          )
        },
        template: 'reported',
      },
      {
        identifier: 'availableAmountCents',
        title: i18n.t('budgets.detail.tabs.employees.table.available'),
        kind: ProjectionKind.Custom,
        justifyContent: 'flexEnd',
        render: ({ availableAmountCents, status }) => {
          if (!status || !availableAmountCents) return
          return (
            <Box gap="s16" flexDirection="row" alignItems="center" textAlign="right">
              <Box width="full" aria-label={i18n.t('budgets.detail.employee_available_amount')}>
                <Text.Normal
                  weight="medium"
                  color={status === 'over_allocation' ? allocationStatusColor(status) : 'grey800'}
                >
                  {amountWithCurrency(Math.abs(availableAmountCents), currency, locale)}
                </Text.Normal>
              </Box>
              <Box width="fit-content">
                <InfoDot size="small" color={allocationStatusColor(status)} />
              </Box>
            </Box>
          )
        },
        template: 'reported',
      },
      {
        identifier: 'actions',
        title: '',
        kind: ProjectionKind.Custom,
        render: (row) => {
          return (
            <Box alignItems="flexEnd">
              <Button.Decorative
                type="secondary"
                onKeyDown={() => handleEmployeeDetailClick(String(row?.id))}
                onPointerDown={() => handleEmployeeDetailClick(String(row?.id))}
                iconName="arrow-right"
                aria-label={i18n.t('budgets.detail.view_employee_details')}
              />
            </Box>
          )
        },
        template: 'actions',
      },
    ],
    {
      variables: {
        id: String(budgetId),
        search: debouncedSearch,
        periodStart: periodStart,
        periodEnd: periodEnd,
      },
    }
  )

  const filters = [TeamFilter({ source: source, teams: teams })]

  return (
    <DataTable.Provider
      source={source}
      sort={{ path: 'id', direction: 'asc' }}
      filters={filters}
      pagination={{
        kind: 'cursor',
        itemsPerPage: 25,
        infiniteScroll: true,
      }}
    >
      <Box gap="s16">
        <DatatableDataContainer
          handleCloseDeleteModal={() => {
            toggleDeleteModal(false)()
            setAllocationId(undefined)
          }}
          handleCloseAllocationPerEmployeeModal={() => {
            toggleAllocationPerEmployeeModal(false)()
          }}
          handleRemoveAllocationModal={() => {
            toggleRemoveAllocationModal(false)()
          }}
          budget={budget}
          openDeleteModal={openDeleteModal}
          openAllocationPerEmployeeModal={openAllocationPerEmployeeModal}
          openRemoveAllocationModal={openRemoveAllocationModal}
          allocationId={allocationId}
          allocations={allocations}
          refetchBudget={refetchBudget}
        />

        {allocationId && (
          <EmployeeDetail
            visible={!!allocationId}
            allocationId={String(allocationId)}
            budget={budget}
            handleClose={() => {
              setAllocationId(undefined)
            }}
            toggleDeleteModal={toggleDeleteModal}
            goToRecordsTab={goToRecordsTab}
            periodStart={periodStart}
            periodEnd={periodEnd}
            refetchBudget={refetchBudget}
          />
        )}
        <DataTableHeader
          toggleDeleteModal={toggleDeleteModal}
          toggleAllocationPerEmployeeModal={toggleAllocationPerEmployeeModal}
          toggleRemoveAllocationModal={toggleRemoveAllocationModal}
          search={search}
          onSearch={onSearch}
        />
        <DataTable.Table
          selectable={() => budget._policies?.update?.can === true}
          emptyState={<EmptyEmployees openEmployeeModal={openEmployeeModal} />}
        />
      </Box>
    </DataTable.Provider>
  )
}

export default EmployeesTab
